import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Polygon, useMap, useMapEvents, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, Plus, Trash2, MapPin, X, Save, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

const AU_CENTER = [-33.865143, 151.209900];  // Sydney

function DrawHandler({ points, setPoints, active }) {
  useMapEvents({
    click(e) {
      if (!active) return;
      setPoints([...points, [e.latlng.lat, e.latlng.lng]]);
    },
  });
  return null;
}

function FitBounds({ polygon }) {
  const map = useMap();
  useEffect(() => {
    if (polygon.length >= 2) {
      map.fitBounds(L.latLngBounds(polygon), { padding: [40, 40] });
    }
  }, [polygon, map]);
  return null;
}

export default function AdminGeofences() {
  const [fences, setFences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/telematics/geofences");
      setFences(data);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeFence = async (id) => {
    if (!window.confirm("Delete this geofence?")) return;
    try {
      await api.delete(`/admin/telematics/geofences/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const toggleActive = async (fence) => {
    try {
      await api.patch(`/admin/telematics/geofences/${fence.id}`, { active: !fence.active });
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-geofences-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <Link to="/admin/fleet-map" className="text-xs text-[#D4AF37] hover:text-[#F6ECCC] tracking-wide">Fleet Map →</Link>
            <Link to="/admin" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37]"><ArrowLeft size={14} /> Back to Dashboard</Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        <div className="label-eyebrow mb-3">FLEET · Geofencing</div>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <h1 className="font-display text-4xl md:text-5xl" style={{ fontWeight: 300 }}>
            Geo<span className="italic text-[#D4AF37]">fences</span>
          </h1>
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="admin-geofence-new"
            className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-[#050505] font-semibold rounded px-4 py-2 text-xs tracking-widest hover:bg-[#E5C158]"
          ><Plus size={13} /> NEW GEOFENCE</button>
        </div>

        {loading ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : fences.length === 0 ? (
          <div className="border border-white/10 border-dashed rounded-lg p-10 text-center text-white/40 text-sm">
            No geofences yet — click "NEW GEOFENCE" and draw a polygon on the map.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {fences.map((f) => (
              <div key={f.id} className="border border-white/10 rounded-lg overflow-hidden bg-[#0a0a0a]">
                <div className="h-40 bg-white/5 relative">
                  <MapContainer
                    center={f.polygon?.[0] || AU_CENTER}
                    zoom={12}
                    zoomControl={false}
                    dragging={false}
                    scrollWheelZoom={false}
                    doubleClickZoom={false}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {f.polygon?.length >= 3 && (
                      <Polygon
                        positions={f.polygon}
                        pathOptions={{
                          color: f.type === "restricted" ? "#ef4444" : "#22c55e",
                          weight: 2, fillOpacity: 0.2,
                        }}
                      />
                    )}
                    <FitBounds polygon={f.polygon || []} />
                  </MapContainer>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`inline-block text-[10px] tracking-widest uppercase px-2 py-0.5 border rounded ${
                        f.type === "restricted" ? "text-red-400 border-red-500/30 bg-red-500/10" : "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
                      }`}>
                        {f.type}
                      </div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-xs text-white/60">
                      <input
                        type="checkbox"
                        checked={f.active}
                        onChange={() => toggleActive(f)}
                        data-testid={`admin-geofence-active-${f.id}`}
                        className="accent-[#D4AF37]"
                      />
                      Active
                    </label>
                  </div>
                  <div className="mt-2 font-display text-lg text-white">{f.name}</div>
                  <div className="text-xs text-white/50 mt-0.5">Alert on: {f.alert_on} · {f.polygon?.length || 0} vertices</div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                    <button
                      onClick={() => { setEditing(f); setShowForm(true); }}
                      data-testid={`admin-geofence-edit-${f.id}`}
                      className="text-xs text-[#D4AF37] hover:text-[#F6ECCC] tracking-widest"
                    >EDIT →</button>
                    <button
                      onClick={() => removeFence(f.id)}
                      data-testid={`admin-geofence-delete-${f.id}`}
                      className="text-white/50 hover:text-red-400"
                    ><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {showForm && (
        <GeofenceModal
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function GeofenceModal({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [type, setType] = useState(initial?.type || "restricted");
  const [alertOn, setAlertOn] = useState(initial?.alert_on || "both");
  const [points, setPoints] = useState(initial?.polygon || []);
  const [active, setActive] = useState(initial?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [drawMode, setDrawMode] = useState(!initial);
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    if (!name.trim()) return toast.error("Name required");
    if (points.length < 3) return toast.error("Draw at least 3 points on the map");
    setSaving(true);
    try {
      const payload = { name: name.trim(), type, polygon: points, alert_on: alertOn, active };
      if (initial) {
        await api.patch(`/admin/telematics/geofences/${initial.id}`, payload);
        toast.success("Geofence updated");
      } else {
        await api.post("/admin/telematics/geofences", payload);
        toast.success("Geofence created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 bg-black/80 z-[1000] flex items-center justify-center p-4"
    >
      <div data-testid="admin-geofence-modal" className="bg-[#0a0a0a] border border-white/10 rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="text-[#D4AF37] text-[10px] tracking-widest">{initial ? "EDIT" : "NEW"} GEOFENCE</div>
            <div className="font-display text-xl">Draw a boundary on the map</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="block col-span-2">
              <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">NAME</span>
              <input
                data-testid="admin-geofence-name"
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Sydney metro"
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">TYPE</span>
              <select
                data-testid="admin-geofence-type"
                value={type} onChange={(e) => setType(e.target.value)}
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              >
                <option value="restricted">Restricted (no-go)</option>
                <option value="allowed">Allowed (safe zone)</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">ALERT ON</span>
              <select
                data-testid="admin-geofence-alert-on"
                value={alertOn} onChange={(e) => setAlertOn(e.target.value)}
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              >
                <option value="enter">Enter</option>
                <option value="exit">Exit</option>
                <option value="both">Both</option>
              </select>
            </label>
          </div>

          <div className="border border-white/10 rounded overflow-hidden h-[440px] relative">
            <MapContainer
              center={points[0] || AU_CENTER}
              zoom={12}
              style={{ height: "100%", width: "100%" }}
              data-testid="admin-geofence-map"
            >
              <TileLayer
                attribution='&copy; OSM'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <DrawHandler points={points} setPoints={setPoints} active={drawMode} />
              {points.length >= 3 && (
                <Polygon
                  positions={points}
                  pathOptions={{
                    color: type === "restricted" ? "#ef4444" : "#22c55e",
                    weight: 2, fillOpacity: 0.18,
                  }}
                />
              )}
              {points.map((p, i) => (
                <Marker key={i} position={p} />
              ))}
              {initial && <FitBounds polygon={initial.polygon || []} />}
            </MapContainer>

            <div className="absolute top-2 left-2 z-[500] bg-[#050505]/90 border border-white/10 rounded p-2 text-[11px] text-white/70 backdrop-blur">
              {drawMode
                ? `Click the map to add points · ${points.length} placed`
                : "Editing paused — click 'Resume drawing' to continue"}
            </div>
            <div className="absolute top-2 right-2 z-[500] flex gap-2">
              <button
                onClick={() => setDrawMode(!drawMode)}
                data-testid="admin-geofence-toggle-draw"
                className="bg-[#050505]/90 border border-white/10 text-white text-[10px] tracking-widest px-3 py-1.5 rounded hover:border-[#D4AF37]/40"
              >
                {drawMode ? "PAUSE" : "RESUME DRAWING"}
              </button>
              <button
                onClick={() => setPoints(points.slice(0, -1))}
                data-testid="admin-geofence-undo"
                disabled={points.length === 0}
                className="bg-[#050505]/90 border border-white/10 text-white text-[10px] tracking-widest px-3 py-1.5 rounded hover:border-[#D4AF37]/40 disabled:opacity-40 inline-flex items-center gap-1"
              ><Undo2 size={11} /> UNDO</button>
              <button
                onClick={() => setPoints([])}
                data-testid="admin-geofence-clear"
                className="bg-[#050505]/90 border border-white/10 text-red-300 text-[10px] tracking-widest px-3 py-1.5 rounded hover:border-red-500/40"
              >CLEAR</button>
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-white/70">
            <input
              type="checkbox" checked={active}
              onChange={(e) => setActive(e.target.checked)}
              data-testid="admin-geofence-active"
              className="accent-[#D4AF37]"
            />
            Active — evaluate against incoming locations
          </label>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 text-xs tracking-widest text-white/70 hover:text-white">CANCEL</button>
          <button
            onClick={submit}
            data-testid="admin-geofence-save"
            disabled={saving}
            className="bg-[#D4AF37] text-[#050505] font-semibold rounded px-5 py-2 text-xs tracking-widest hover:bg-[#E5C158] disabled:opacity-50 inline-flex items-center gap-1"
          ><Save size={12} /> {saving ? "SAVING…" : "SAVE GEOFENCE"}</button>
        </div>
      </div>
    </div>
  );
}
